import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

interface GameLayoutProps {
  children: React.ReactNode;
}

function getBackPath(pathname: string, search: string): string | null {
  if (pathname === '/game/opening') {
    return '/game';
  }
  if (pathname === '/game/character-select') {
    return `/game/opening${search}`;
  }
  if (pathname === '/game/rules') {
    return `/game/character-select${search}`;
  }
  if (pathname.startsWith('/game/play/') && pathname.split('/').length === 4) {
    return `/game/rules${search}`;
  }
  return null;
}

const GameLayout: React.FC<GameLayoutProps> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, loading } = useAuth();
  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);

  const backPath = getBackPath(location.pathname, location.search);

  const handleBack = () => {
    if (backPath) {
      navigate(backPath);
    }
  };

  const handleConfirmLeave = () => {
    navigate('/');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div
        className="relative min-h-screen flex items-center justify-center bg-cover bg-center px-4"
        style={{ backgroundImage: "url('/backgrounds/game-landing-bg.svg')" }}
      >
        <div className="fixed top-4 left-4 z-[150]">
          <button
            onClick={() => navigate('/')}
            className="px-4 py-1.5 rounded-full text-sm font-medium text-white hover:text-white transition font-caudex"
            style={{ background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)', border: '1px solid rgba(255,255,255,0.25)' }}
          >
            Leave Game
          </button>
        </div>
  
        <div className="w-full max-w-md bg-[#1b2a41] text-white rounded-2xl p-8 shadow-2xl text-center">
          <h2 className="text-xl font-bold mb-3 font-caudex">Login Required</h2>
          <p className="text-sm text-white/70 mb-2 font-caudex">
            You need to be logged in to play Earth on Trial.
          </p>
          <p className="text-sm text-white/70 mb-6 font-caudex">
            Don&apos;t have an account? Please register below.
          </p>
          <div className="flex justify-center gap-3">
            <button
              onClick={() => navigate('/login', { state: { from: '/game' } })}
              className="px-5 py-2 rounded-full bg-teal-500 hover:bg-teal-600 transition font-semibold text-sm font-caudex"
            >
              Log In
            </button>
            <button
              onClick={() => navigate('/register', { state: { from: '/game' } })}
              className="px-5 py-2 rounded-full border border-white/30 hover:bg-white/10 transition font-semibold text-sm font-caudex"
            >
              Register
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative">
      <div className="fixed top-4 left-4 z-[150] flex items-center gap-2">
        {backPath && (
          <button
            onClick={handleBack}
            className="px-4 py-1.5 rounded-full text-sm font-medium text-white hover:text-white transition font-caudex"
            style={{ background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)', border: '1px solid rgba(255,255,255,0.25)' }}
          >
            ← Back
          </button>
        )}
        <button
          onClick={() => setShowLeaveConfirm(true)}
          className="px-4 py-1.5 rounded-full text-sm font-medium text-white hover:text-white transition font-caudex"
          style={{ background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)', border: '1px solid rgba(255,255,255,0.25)' }}
        >
          Leave Game
        </button>
      </div>

      {children}

      {showLeaveConfirm && (
        <div className="fixed inset-0 z-[250] flex items-center justify-center bg-black/60 backdrop-blur-sm px-4">
          <div className="bg-[#1b2a41] text-white rounded-2xl p-8 max-w-sm w-full shadow-2xl text-center">
            <h2 className="text-xl font-bold mb-3 font-caudex">Leave the Game?</h2>
            <p className="text-sm text-white/70 mb-6 font-caudex">
              You&apos;re about to leave the game. Any unfinished progress won&apos;t be saved, and this may still count as an attempt used. Please confirm before continuing.
            </p>
            <div className="flex justify-center gap-3">
              <button
                onClick={() => setShowLeaveConfirm(false)}
                className="px-5 py-2 rounded-full border border-white/30 hover:bg-white/10 transition font-semibold text-sm font-caudex"
              >
                Stay
              </button>
              <button
                onClick={handleConfirmLeave}
                className="px-5 py-2 rounded-full bg-red-500 hover:bg-red-600 transition font-semibold text-sm font-caudex"
              >
                Leave
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GameLayout;